<template>
    <div class="text-ink text-xs sm:text-sm font-light font-gotham">
        <div
            v-if="showSwatch"
            :style="backgroundStyles"
            class="ml-auto rounded-sm border border-gray-light w-14 h-11 bg-cover bg-no-repeat"
        >
            <img
                v-if="!mainSelection"
                src="../assets/images/icons/slash.svg"
                width="55"
                height="44"
                alt="slash"
            >
        </div>

        <p v-else>
            <span v-if="secondaryLabel && mainLabel !== secondaryLabel">
                {{ mainLabel }} /<br>{{ secondaryLabel }}
            </span>
            <span v-else>
                {{ mainLabel }}
            </span>
        </p>
    </div>
</template>

<script>
    import allStores from '../stores/all';

    import { autoWebp } from '../helpers/images';

    export default {
        mixins: [allStores],

        props: {
            placementData: {
                type: Object,
                required: true,
            },
        },

        computed: {
            isLogo() {
                return this.customizerStore.selectedPersonalizationType(this.placementData) === 'personalization-logo';
            },

            isSwatch() {
                if (this.isLogo) {
                    return true;
                }

                // Swatch placement...
                if (this.placementData.placements[0].custom['fj-placement-type'] === 'swatch') {
                    return true;
                }

                return false;
            },

            showSwatch() {
                if (this.isLogo) {
                    return true;
                }

                // Display text instead of swatches for placements with the large swatch variant
                const isLarge = this.placementData.placements[0].custom['fj-placement-variant'] === 'large';

                return this.isSwatch && !isLarge;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            selectionWasMade() {
                return this.customizerStore.selectionsMade.includes(this.placementData.name);
            },

            mainSelection() {
                if (this.customizerStore.isPersonalizationGroup(this.placementData)) {
                    return this.customizerStore.selectedPersonalization(this.placementData);
                }

                const selectedAtPlacement = this.viewController?.selectedAtPlacement(this.placementData.placements[0].code);
                const selection = selectedAtPlacement?.placeholder ? null : selectedAtPlacement;

                return selection;
            },

            secondarySelection() {
                if (this.customizerStore.isPersonalizationGroup(this.placementData)) {
                    return null;
                }

                let selectedAtPlacement = {};
                let selection = null;

                if (this.placementData.placements.length > 1) {
                    selectedAtPlacement = this.viewController?.selectedAtPlacement(this.placementData.placements[1].code);
                    selection = selectedAtPlacement?.placeholder ? null : selectedAtPlacement;
                }

                return selection;
            },

            mainLabel() {
                if (this.customizerStore.isPersonalizationGroup(this.placementData) && this.selectionWasMade) {
                    // Special case for handling personalization types.

                    switch (this.customizerStore.selectedPersonalizationType(this.placementData)) {
                    case 'personalization-none': return this.$t('PERSONALIZE_NONE');
                    case 'personalization-logo': {
                        return this.mainSelection ? this.getDescriptionText(this.mainSelection.description, this.mainSelection) : '';
                    }
                    case 'personalization-monogram': {
                        return this.mainSelection ? this.mainSelection.custom['personalization-text'] : '';
                    }
                    default:
                    }
                }

                return this.mainSelection && this.selectionWasMade ? this.getDescriptionText(this.mainSelection.description, this.mainSelection) : '——';
            },

            secondaryLabel() {
                return this.secondarySelection ? this.getDescriptionText(this.secondarySelection.description, this.secondarySelection) : '';
            },

            backgroundStyles() {
                return {
                    backgroundColor: this.mainSelection?.custom?.hex,
                    backgroundImage: `url(${autoWebp(`${this.mainSelection?.custom?.image}?rwid=112&rhei=88`)})`,
                };
            },

            gender() {
                return this.customizerStore.product?.gender;
            },

            sizeTranslations() {
                return this.customizerStore.sizeTranslations.dictionary.item;
            },
        },

        methods: {
            getDescriptionText(description, option) {
                let text = '';

                description.forEach((desc) => {
                    if (desc.definition === 'Size') {
                        text = this.getPrettySize(desc.name, option);
                    } else if (desc.definition === 'Width') {
                        const shortened = desc.name.replace(/ *$/, '');

                        text += ` ${shortened}`;
                    } else {
                        text += ` ${desc.name}`;
                    }
                });

                return text.trim();
            },

            getPrettySize(shoeSize, option) {
                let prettySize = '';

                let useName = shoeSize;

                if (this.gender === 'F' && option.custom?.['fj-size-f-name']) {
                    useName = option.custom?.['fj-size-f-name'];
                }

                if (this.gender === 'M' && option.custom?.['fj-size-m-name']) {
                    useName = option.custom?.['fj-size-m-name'];
                }

                const matchingSize = this.sizeTranslations.find((size) => size['@description'] === useName);

                if (matchingSize) {
                    const matchingSizeText = matchingSize['#text'];

                    // See StepOptionsSize.vue@getButtonLabelText
                    prettySize = matchingSizeText.replace(/ US$/, '').replace(/\.0/g, '').trim();
                }

                return prettySize;
            },
        },
    };
</script>
